import React, { useMemo } from 'react';
import { Employee, Lead, ProInvoice, ProJob, Quote, TimeEntry } from '../../types/pro';
import { kr, quoteTotal } from '../../utils/pro';
import { invoiceTotal } from '../../utils/invoice';
import { PageHeader } from '../../components/pro/PageHeader';
import { BarChart } from '../../components/pro/BarChart';
import { RankedBars } from '../../components/pro/RankedBars';
interface ProStatisticsProps {
  leads: Lead[];
  quotes: Quote[];
  jobs: ProJob[];
  invoices: ProInvoice[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  vatRate: number;
}
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
export function ProStatistics({
  leads,
  quotes,
  jobs,
  invoices,
  employees,
  timeEntries,
  vatRate
}: ProStatisticsProps) {
  const invoiceTotalOf = (invoice: ProInvoice) => invoiceTotal(invoice);
  const byMonth = useMemo(() => {
    const now = new Date();
    return Array.from({
      length: 6
    }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      const inMonth = (iso: string) => {
        const value = new Date(iso);
        return value.getMonth() === date.getMonth() && value.getFullYear() === date.getFullYear();
      };
      return {
        label: MONTHS[date.getMonth()],
        revenue: invoices.filter(i => i.status !== 'kladde' && inMonth(i.invoiceDate)).reduce((sum, i) => sum + invoiceTotalOf(i), 0),
        leads: leads.filter(lead => inMonth(lead.createdDate)).length
      };
    });
  }, [invoices, leads, vatRate]);
  const kpis = useMemo(() => {
    const decided = leads.filter(lead => ['vundet', 'tabt'].includes(lead.stage));
    const won = decided.filter(lead => lead.stage === 'vundet');
    const answered = quotes.filter(quote => ['accepteret', 'afvist'].includes(quote.status));
    const accepted = answered.filter(quote => quote.status === 'accepteret');
    const revenue = invoices.filter(i => i.status !== 'kladde').reduce((sum, i) => sum + invoiceTotalOf(i), 0);
    const billable = timeEntries.filter(entry => entry.billable).reduce((sum, entry) => sum + entry.hours, 0);
    const allHours = timeEntries.reduce((sum, entry) => sum + entry.hours, 0);
    return {
      conversion: decided.length ? Math.round(won.length / decided.length * 100) : 0,
      acceptance: answered.length ? Math.round(accepted.length / answered.length * 100) : 0,
      averageJob: invoices.length ? revenue / invoices.length : 0,
      billable,
      billableShare: allHours ? Math.round(billable / allHours * 100) : 0,
      revenue,
      newLeads: leads.length
    };
  }, [leads, quotes, invoices, timeEntries, vatRate]);

  /** Revenue by source, not lead count — ten cheap leads beat nothing. */
  const bySource = useMemo(() => {
    const map = new Map<string, {
      value: number;
      count: number;
    }>();
    leads.forEach(lead => {
      const current = map.get(lead.source) ?? {
        value: 0,
        count: 0
      };
      map.set(lead.source, {
        value: current.value + (lead.stage === 'vundet' ? lead.estimatedValue : 0),
        count: current.count + 1
      });
    });
    return Array.from(map.entries()).map(([label, data]) => ({
      label,
      value: data.value,
      meta: `${data.count} leads`
    }));
  }, [leads]);
  const sourceVolume = useMemo(() => {
    const map = new Map<string, number>();
    leads.forEach(lead => map.set(lead.source, (map.get(lead.source) ?? 0) + 1));
    return Array.from(map.entries()).map(([label, value]) => ({
      label,
      value
    }));
  }, [leads]);
  const byEmployee = useMemo(() => employees.map(employee => {
    const hours = timeEntries.filter(entry => entry.employeeId === employee.id && entry.billable).reduce((sum, entry) => sum + entry.hours, 0);
    return {
      label: employee.name,
      value: hours * employee.hourlyRate,
      meta: `${hours.toLocaleString('da-DK')} t`
    };
  }), [employees, timeEntries]);
  const card = 'rounded-xl border border-stone-200 bg-white p-5';
  return <div className="space-y-5" data-id="element-2172" data-mp="14oeukeg3s83w">
      <PageHeader title="Statistik" subtitle="Hvor kommer pengene fra, og hvad koster de at hente" data-id="element-2173" data-mp="14p83nm2jb9p7" />

      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-stone-200 bg-stone-200 lg:grid-cols-6" data-id="element-2174" data-mp="21ga2pnc3i8n6">
        {[{
        label: 'Omsætning i alt',
        value: kr(kpis.revenue)
      }, {
        label: 'Nye leads',
        value: kpis.newLeads
      }, {
        label: 'Konvertering',
        value: `${kpis.conversion} %`
      }, {
        label: 'Accepterede tilbud',
        value: `${kpis.acceptance} %`
      }, {
        label: 'Gns. opgavestørrelse',
        value: kr(kpis.averageJob)
      }, {
        label: 'Fakturerbare timer',
        value: `${kpis.billable.toLocaleString('da-DK')} t`
      }].map(kpi => <div key={kpi.label} className="bg-white p-4" data-id="element-2175" data-mp="6u48hmct80ot">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-2176" data-mp="6unkynjoxwvg">
              {kpi.label}
            </p>
            <p className="mt-1.5 text-xl font-bold tabular-nums text-stone-900" data-id="element-2177" data-mp="futzmcr4l2w7">
              {kpi.value}
            </p>
          </div>)}
      </section>

      {/* The headline question: which sources actually earn money */}
      <section className={card} data-id="element-2178" data-mp="prymyfwpj4el">
        <h2 className="text-base font-semibold text-stone-900" data-id="element-2179" data-mp="pr5b50iw5opl">
          Omsætning pr. leadkilde
        </h2>
        <p className="mb-5 text-sm text-stone-500" data-id="element-2180" data-mp="umccw2slojbv">
          Vundet værdi, ikke antal henvendelser — det er her
          markedsføringskronerne skal hen.
        </p>
        <RankedBars data={bySource} format={kr} data-id="element-2181" data-mp="13m7rkhld6wgn" />
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-id="element-2182" data-mp="18iy6v2pcho10">
        <section className={card} data-id="element-2183" data-mp="18herdebijh0a">
          <h2 className="text-base font-semibold text-stone-900" data-id="element-2184" data-mp="34m4bee1dn65j">
            Omsætning pr. måned
          </h2>
          <p className="mb-5 text-sm text-stone-500" data-id="element-2185" data-mp="2zoueyefklu87">Seneste 6 måneder</p>
          <BarChart data={byMonth.map(m => ({
          label: m.label,
          value: m.revenue
        }))} format={kr} data-id="element-2186" data-mp="3ed7bpdmhfh3r" />
        </section>

        <section className={card} data-id="element-2187" data-mp="39knxcefz0hxw">
          <h2 className="text-base font-semibold text-stone-900" data-id="element-2188" data-mp="3nd3tqwed9hpu">
            Nye leads pr. måned
          </h2>
          <p className="mb-5 text-sm text-stone-500" data-id="element-2189" data-mp="3jcbla5dbjgum">Seneste 6 måneder</p>
          <BarChart data={byMonth.map(m => ({
          label: m.label,
          value: m.leads
        }))} format={value => `${value} leads`} data-id="element-2190" data-mp="165i9srm424s" />
        </section>

        <section className={card} data-id="element-2191" data-mp="3sd9r8t7bhyfu">
          <h2 className="mb-5 text-base font-semibold text-stone-900" data-id="element-2192" data-mp="22xa6sfy90mkw">
            Omsætning pr. medarbejder
          </h2>
          <RankedBars data={byEmployee} format={kr} data-id="element-2193" data-mp="1xzh4vz3zaeqf" />
        </section>

        <section className={card} data-id="element-2194" data-mp="dgoigpxela7b">
          <h2 className="mb-5 text-base font-semibold text-stone-900" data-id="element-2195" data-mp="dhpscr2onqng">
            Antal leads pr. kilde
          </h2>
          <RankedBars data={sourceVolume} format={value => `${value}`} data-id="element-2196" data-mp="iepyf1zj8tek" />
        </section>
      </div>
    </div>;
}